import { Box, FormHelperText, Grid, IconButton } from '@mui/material';
import { EyeIcon, EyeSlashIcon } from '@synergy/theme';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { useState } from 'react';

import * as ContactPermissionActions from '../store/contact-detail-permissions-actions';
import { ContactDetailPermissionsImpersonateHeader } from './contact-detail-permissions-impersonate-header';
import { ContactPermissionsFormValues } from '../models/contact-permissions-form-values';
import { contactDetailOverviewFormDataSelector } from '../../overview/store/contact-detail-overview-selectors';
import {
    contactPermissionsFormDataSelector,
    contactPermissionsHasUserSelector,
} from '../store/contact-detail-permissions-selectors';
import { contactPermissionsSelector } from '../../store/contact-detail-global-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

export const ContactDetailPermissionsCredentials = () => {
    const hasUser = useAppSelector(contactPermissionsHasUserSelector);
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const { formValues } = useAppSelector(contactDetailOverviewFormDataSelector);
    const { errors } = useAppSelector(contactPermissionsFormDataSelector);
    const { permissions } = useAppSelector(contactPermissionsSelector);
    const relatedAccounts = useAppSelector(
        state => state.contactsAndOrganizations.contacts.contactDetail.overview.relatedAccounts
    );
    const [isVisible, setIsVisible] = useState(false);
    const dispatch = useThunkDispatch();
    const formik = useFormikContext<ContactPermissionsFormValues>();

    const handleToggle = (): void => {
        setIsVisible(!isVisible);
    };

    const handleInvitation = () => {
        if (!hasUser) {
            dispatch(ContactPermissionActions.inviteContact());
        } else {
            if (formik.dirty) {
                dispatch(
                    ContactPermissionActions.updateContactPermissions(
                        formik.values,
                        formik.dirty,
                        formik.isValid,
                        formik.errors
                    )
                );
            }
            dispatch(ContactPermissionActions.reInviteContact());
        }
    };

    const localeOptions: SelectOption[] = availableLocales.map(value => ({
        value,
        displayValue: <FormattedMessage id={`app.common.${value}`} />,
    }));

    const mainEmailError = errors?.mainEmail as unknown as { contactName: string; contactId: number };

    const invitationMessage = !hasUser
        ? 'app.contactsAndOrganizations.contact.credentialsHintInvite'
        : 'app.contactsAndOrganizations.contact.credentialsHintReInvite';
    const noOrganizationMessage =
        relatedAccounts.length === 0 ? 'app.contactsAndOrganizations.contact.credentialsHintNoOrganization' : null;

    const hideInvitation = permissions?.isActivated === true && hasUser;
    const userActive = permissions?.isActivated === true;

    return (
        <>
            <ContactDetailPermissionsImpersonateHeader />

            {!hideInvitation && (
                <PoolTypography typography="body1" id={noOrganizationMessage ?? invitationMessage} sx={{ mb: 2 }} />
            )}

            <Grid container={true} spacing={2} alignItems="center">
                <Grid item={true} xs={6}>
                    <PoolTextField
                        value={formValues.mainEmail}
                        label={<FormattedMessage id="app.label.email" />}
                        error={!!errors.mainEmail}
                        helperText={
                            mainEmailError ? (
                                <FormattedMessage
                                    id="app.error.uniqueLogin"
                                    values={{
                                        name: mainEmailError.contactName,
                                        id: mainEmailError.contactId,
                                    }}
                                />
                            ) : null
                        }
                        fullWidth={true}
                        disabled={true}
                    />
                </Grid>
                <Grid item={true} xs={6}>
                    <Field name="password">
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.contactsAndOrganizations.contact.newPassword" />}
                                fullWidth={true}
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                type={isVisible ? 'text' : 'password'}
                                disabled={!userActive}
                                endAdornment={
                                    <IconButton onClick={handleToggle} disabled={!userActive}>
                                        {isVisible ? <EyeSlashIcon /> : <EyeIcon />}
                                    </IconButton>
                                }
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={6}>
                    <Field name="locale">
                        {({ field, form, meta }: FieldProps<string>) => (
                            <Box sx={{ flex: '1 0 0' }}>
                                <PoolSelect
                                    name={field.name}
                                    fullWidth={true}
                                    label={<FormattedMessage id="app.label.systemLocale" />}
                                    options={localeOptions}
                                    onValueChange={value => form.setFieldValue(field.name, value)}
                                    error={meta.touched && !!meta.error}
                                    value={field.value}
                                    noSorting={true}
                                    disallowDefaultValue={true}
                                    disabled={!hasUser}
                                />
                                {meta.error && (
                                    <FormHelperText error={true}>
                                        <FormattedMessage id={meta.error} />
                                    </FormHelperText>
                                )}
                            </Box>
                        )}
                    </Field>
                </Grid>

                {!hideInvitation && (
                    <Grid item={true} xs={6}>
                        <PoolButton styleType="firstLevel" variant="contained" onClick={handleInvitation}>
                            <FormattedMessage
                                id={
                                    !hasUser
                                        ? 'app.contactsAndOrganizations.contact.inviteUser'
                                        : 'app.contactsAndOrganizations.contact.reInviteUser'
                                }
                            />
                        </PoolButton>
                    </Grid>
                )}
            </Grid>
        </>
    );
};
